import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "./supabaseClient";
import Login from "./pages/Login";
import Gallery from "./pages/Gallery";
import Albums from "./pages/Albums";
import Upload from "./pages/Upload";
import Admin from "./pages/Admin";
import Signup from "./pages/Signup";
import SetPassword from "./pages/SetPassword";
import Navbar from "./components/Navbar";
import "./App.css";

export default function App() {
  const [session, setSession] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInvite, setIsInvite] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("type=invite") || hash.includes("type=recovery")) {
      setIsInvite(true);
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchRole(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === "SIGNED_IN" && isInvite) return;
      setSession(session);
      if (session) fetchRole(session.user.id);
      else { setRole(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchRole(userId) {
    const { data } = await supabase
      .from("user_roles")
      .select("role, expires_at")
      .eq("user_id", userId)
      .single();

    if (data?.expires_at && new Date(data.expires_at) < new Date()) {
      await supabase.auth.signOut();
      setSession(null);
      setRole(null);
      setLoading(false);
      return;
    }

    // Check session age - sign out after 24 hours
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const sessionAge = Date.now() - new Date(session.user.last_sign_in_at).getTime();
      const twentyFourHours = 24 * 60 * 60 * 1000;
      if (sessionAge > twentyFourHours) {
        await supabase.auth.signOut();
        setSession(null);
        setRole(null);
        setLoading(false);
        return;
      }
    }

    setRole(data?.role || "readonly");
    setLoading(false);
  }

  const canUpload = ["admin", "uploader"].includes(role);
  const canDelete = role === "admin";
  const isAdmin   = role === "admin";

  if (isInvite) return <SetPassword />;

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!session) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/signup" element={<Signup />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <div className="app">
        <Navbar role={role} canUpload={canUpload} isAdmin={isAdmin} />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Gallery role={role} canDelete={canDelete} />} />
            <Route path="/albums" element={<Albums role={role} canDelete={canDelete} />} />
            <Route path="/upload" element={canUpload ? <Upload role={role} /> : <Navigate to="/" />} />
            <Route path="/admin" element={isAdmin ? <Admin /> : <Navigate to="/" />} />
            <Route path="/signup" element={<Navigate to="/" />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}
